import { useSyncExternalStore } from 'react';
import { create } from 'zustand';

/**
 * FRONT-3223: the whole UI refactor (FRONT-3138) ships behind ONE org-scoped
 * Unleash flag, `eddy.ui-refactoring` (registered in
 * `src/core/flag-registry.ts`). On: every refactored surface. Off: every old
 * one — kept as `*-legacy` modules, or at main's own paths for the Build
 * window and its question card.
 *
 * Why a store and not `useFeatureFlag`: the value is read by stores and plain
 * functions (`window-payloads.ts`, `pro-builder-open-store.ts`, the theme
 * store) as well as components, and it must not change mid-session — a live
 * flip would orphan every open window's host. The app shell seeds it ONCE
 * from `/api/feature-flags` before it renders anything refactored
 * (`layouts/app-layout.tsx`). Unseeded means off: "off means the old UI,
 * never a greyed-out new one". The frontend test setup
 * (`src/test/setup-ui-refactoring.ts`) seeds it on; a test of an old path
 * calls `setUiRefactoring(false)`.
 */
export const UI_REFACTORING_FLAG = 'eddy.ui-refactoring';

interface UiRefactoringState {
  /** True once the app shell has read the evaluated flags (or given up on them). */
  seeded: boolean;
  on: boolean;
}

export const useUiRefactoringStore = create<UiRefactoringState>(() => ({ seeded: false, on: false }));

/**
 * Dev builds only: `?ui-refactoring=off` (or `=on`) overrides the flag for this
 * browser until `?ui-refactoring=` clears it — a local stack has no Unleash, so
 * nothing sets the flag and it reads off (the old UI); `=on` is the way to the
 * new one there. Production builds ignore it.
 */
export const UI_REFACTORING_OVERRIDE_KEY = 'eddy-ui-refactoring';

export function parseUiRefactoringOverride(raw: string | null): boolean | null {
  const v = raw?.trim().toLowerCase();
  if (v === 'on') return true;
  if (v === 'off') return false;
  return null;
}

function localOverride(): boolean | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null;
  try {
    const param = new URLSearchParams(window.location.search).get('ui-refactoring');
    if (param !== null) {
      if (parseUiRefactoringOverride(param) === null) window.localStorage.removeItem(UI_REFACTORING_OVERRIDE_KEY);
      else window.localStorage.setItem(UI_REFACTORING_OVERRIDE_KEY, param.trim().toLowerCase());
    }
    return parseUiRefactoringOverride(window.localStorage.getItem(UI_REFACTORING_OVERRIDE_KEY));
  } catch {
    return null;
  }
}

/**
 * Seed from the evaluated flags: `isOn` is `resolveFeatureFlag(name, false, …)`
 * for the shell's query. Called once per page load; a flip in Unleash applies on
 * the next load.
 */
export function seedUiRefactoring(isOn: (name: string) => boolean): void {
  useUiRefactoringStore.setState({ seeded: true, on: localOverride() ?? isOn(UI_REFACTORING_FLAG) });
}

/**
 * Component read. Off until seeded.
 *
 * Not `useUiRefactoringStore(selector)`: zustand hands React its INITIAL state
 * as the server snapshot, and a static render (`renderToStaticMarkup`, which the
 * frontend suite uses) reads the server snapshot — so the flag would read off in
 * every component test whatever the test seeded.
 */
export function useUiRefactoring(): boolean {
  const read = () => useUiRefactoringStore.getState().on;
  return useSyncExternalStore(useUiRefactoringStore.subscribe, read, read);
}

/** Read for stores and plain functions (no hook rules). Off until seeded. */
export function isUiRefactoringOn(): boolean {
  return useUiRefactoringStore.getState().on;
}

/** Test helper: set the flag, marked seeded. */
export function setUiRefactoring(on: boolean): void {
  useUiRefactoringStore.setState({ seeded: true, on });
}
